/**
 * Tiny in-memory ring buffer of UI navigation events. Captures only what
 * the audit log doesn't — view changes, modal opens, tab switches — so the
 * bug-report form can show "what was the user just doing" beyond the
 * state-change events that already flow through audit.
 *
 * Module-level singleton: pushBreadcrumb is callable from anywhere in
 * App.js without prop-drilling, and useUiBreadcrumb returns a reactive
 * snapshot for components.
 *
 * Capped at 20 entries (oldest dropped). Not persisted — only matters for
 * an open report form, and surviving a tab close would just confuse the
 * user. Cleared on logout (the consumer can call clearBreadcrumbs).
 *
 * Entry shape:
 *   { ts: number, type: string, value: string, meta?: object }
 *
 * Common types:
 *   'view'   — top-level view changed (select / home / dm / combat / library / ...)
 *   'modal'  — modal opened or closed (value = name; meta.action = 'open'|'close')
 *   'tab'    — tab/sub-section changed within a view
 *   'action' — high-level user action that isn't yet wired through audit
 */

import React from 'react';

const MAX_BREADCRUMBS = 20;
let _breadcrumbs = [];
const _subscribers = new Set();

const emit = () => {
  for (const sub of _subscribers) {
    try { sub(_breadcrumbs); } catch (_) { /* swallow */ }
  }
};

/**
 * Push a breadcrumb. Safe to call from anywhere — no React context needed.
 *
 * @param {string} type
 * @param {string} value
 * @param {object} [meta]
 */
export function pushBreadcrumb(type, value, meta) {
  if (typeof type !== 'string' || !type) return;
  const entry = { ts: Date.now(), type, value: String(value ?? ''), meta };
  _breadcrumbs = [..._breadcrumbs.slice(-(MAX_BREADCRUMBS - 1)), entry];
  emit();
}

/**
 * Clear all breadcrumbs. Call on logout / view-context change so the next
 * session doesn't inherit stale UI history.
 */
export function clearBreadcrumbs() {
  if (_breadcrumbs.length === 0) return;
  _breadcrumbs = [];
  emit();
}

/**
 * Non-reactive snapshot. For use inside event handlers / submit flows
 * where re-rendering on every breadcrumb push would be wasteful.
 */
export function readBreadcrumbsSnapshot() {
  return _breadcrumbs.slice();
}

/**
 * Reactive hook returning the current breadcrumb buffer. Components that
 * display the breadcrumb list (e.g. the bug-report form's "what was I just
 * doing" panel) use this; pure submit handlers should use readBreadcrumbsSnapshot.
 */
export function useUiBreadcrumb() {
  const [, force] = React.useReducer(n => n + 1, 0);
  React.useEffect(() => {
    const sub = () => force();
    _subscribers.add(sub);
    return () => { _subscribers.delete(sub); };
  }, []);
  return _breadcrumbs;
}

if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  const INSTALL_BREADCRUMB_DEBUG_HELPERS_CONFIRMATION = 'INSTALL_BREADCRUMB_DEBUG_HELPERS';
  const BREADCRUMB_DEBUG_CONFIRMATIONS = Object.freeze({
    push: 'PUSH_DEBUG_BREADCRUMB',
    clear: 'CLEAR_DEBUG_BREADCRUMBS',
  });
  const requireBreadcrumbDebugConfirmation = (received, expected, helperName) => {
    if (received === expected) return;
    throw new Error(`${helperName}: pass { confirm: "${expected}" } to run this breadcrumb diagnostic helper.`);
  };

  window.__installBreadcrumbDebugHelpers__ = ({ confirm } = {}) => {
    requireBreadcrumbDebugConfirmation(
      confirm,
      INSTALL_BREADCRUMB_DEBUG_HELPERS_CONFIRMATION,
      '__installBreadcrumbDebugHelpers__'
    );
    const helpers = {
      confirmations: BREADCRUMB_DEBUG_CONFIRMATIONS,
      read: readBreadcrumbsSnapshot,
      push: (type, value, meta, { confirm: helperConfirm } = {}) => {
        requireBreadcrumbDebugConfirmation(helperConfirm, BREADCRUMB_DEBUG_CONFIRMATIONS.push, 'push');
        return pushBreadcrumb(type, value, meta);
      },
      clear: ({ confirm: helperConfirm } = {}) => {
        requireBreadcrumbDebugConfirmation(helperConfirm, BREADCRUMB_DEBUG_CONFIRMATIONS.clear, 'clear');
        return clearBreadcrumbs();
      },
    };
    window.__breadcrumbDebugHelpers__ = helpers;
    console.warn('Installed breadcrumb diagnostics under window.__breadcrumbDebugHelpers__. Mutating helpers require per-call confirmation.');
    return helpers;
  };
  delete window.__breadcrumbs__;
  delete window.__pushBreadcrumb__;
  delete window.__clearBreadcrumbs__;
}
